import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IKpiPreset, defaultValue } from 'app/shared/model/kpi-preset.model';

const initialState: EntityState<IKpiPreset> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/kpi-presets';

// Actions

export const getEntitiesList = createAsyncThunk('kpiPreset/fetch_entity_list_list', async () => {
  const requestUrl = `${apiUrl}-list`;
  return axios.get<IKpiPreset[]>(requestUrl);
});

export const getEntitiesListByCompany = createAsyncThunk('kpiPreset/fetch_entity_list_by_company', async (companyId: number | null) => {
  let requestUrl = `${apiUrl}-list`;
  if (companyId !== null && companyId !== undefined) {
    requestUrl += `?companyId=${companyId}`;
  }
  return axios.get<IKpiPreset[]>(requestUrl);
});

export const createEntity = createAsyncThunk(
  'kpiPreset/create_entity',
  async (data: { entity: IKpiPreset; companyId?: number | null }, thunkAPI) => {
    const { entity, companyId } = data;

    const entityToSend = { ...entity };
    if (companyId) {
      entityToSend.company = { id: companyId };
    }

    const result = await axios.post<IKpiPreset>(apiUrl, cleanEntity(entityToSend));

    if (companyId) {
      thunkAPI.dispatch(getEntitiesListByCompany(companyId));
    } else {
      thunkAPI.dispatch(getEntitiesList());
    }
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'kpiPreset/update_entity',
  async (data: { entity: IKpiPreset; companyId?: number | null }, thunkAPI) => {
    const { entity, companyId } = data;

    const entityToSend = { ...entity };
    if (companyId && !entityToSend.company) {
      entityToSend.company = { id: companyId };
    }

    const result = await axios.put<IKpiPreset>(`${apiUrl}/${entity.id}`, cleanEntity(entityToSend));

    if (companyId) {
      thunkAPI.dispatch(getEntitiesListByCompany(companyId));
    } else {
      thunkAPI.dispatch(getEntitiesList());
    }
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'kpiPreset/delete_entity',
  async (data: { id: string | number; companyId?: number | null }, thunkAPI) => {
    const { id, companyId } = data;
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IKpiPreset>(requestUrl);

    if (companyId) {
      thunkAPI.dispatch(getEntitiesListByCompany(companyId));
    } else {
      thunkAPI.dispatch(getEntitiesList());
    }
    return result;
  },
  { serializeError: serializeAxiosError }
);

// slice
export const KpiPresetSlice = createEntitySlice({
  name: 'kpiPreset',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntitiesList, getEntitiesListByCompany), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntitiesList, getEntitiesListByCompany), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = KpiPresetSlice.actions;
export default KpiPresetSlice.reducer;
